/**
 * response-leak-guard —— AI 内部结构泄漏判定
 *
 * 背景：主链路使用原生工具调用，正常回复是普通散文。旧历史或异常网关仍可能回放
 * `{"actions":[...]}` 这类结构，由 processAIResponse 提取其中的对话文本渲染。
 * 如果结构破损，原始结构可能被当正文返回，
 * 泄漏到聊天气泡（用户看到一坨 `{"actions":...}`，俗称"掉格式"）。
 *
 * 本模块只提供一个纯判定：looksLikeInternalStructure(text)。
 * 它被用在唯一出口 processAIResponse 的兜底分支——当提取失败且原文像内部结构时，
 * 返回占位符而非原始 JSON。防线收敛在这一处，不在下游渲染层撒网。
 *
 * 判定原则：宁可漏放一句正常聊天，也不能把 JSON 原样显示给用户。因此要求
 * "带引号的内部字段名"这种强特征，避免误伤用户正常聊代码/聊 JSON 的场景。
 */

/** 内部结构的强特征字段（带引号，避免误伤正文里恰好出现 actions 这个词） */
const INTERNAL_STRUCTURE_MARKERS = [
  '"actions"',
  '"send_messages"',
  '"relationship_adjustment"',
  '"score_change"',
]

/** 无法解析出正文时展示的友好占位文本 */
export const LEAK_PLACEHOLDER = '[这条消息没能正常显示]'

/**
 * 判断文本是否像 AI 内部结构泄漏。命中任一条件即视为泄漏：
 *   1. 去空白后以 ``` json 代码围栏开头（模型把结构包进了代码块）
 *   2. 去空白后直接以 `{"` 或 `[{` 开头（裸 JSON）
 *   3. 含有带引号的内部字段名（如 "actions"、"send_messages"）
 */
export function looksLikeInternalStructure(text: string): boolean {
  if (typeof text !== 'string' || !text.trim())
    return false

  const trimmed = text.trim()

  // 1. 代码围栏包裹的 JSON
  if (/^```(?:json)?\s*[[{]/.test(trimmed))
    return true

  // 2. 裸 JSON 开头
  if (/^[[{]\s*["[{]/.test(trimmed))
    return true

  // 3. 含内部字段特征
  return INTERNAL_STRUCTURE_MARKERS.some(marker => trimmed.includes(marker))
}
